import {Component, Input, OnInit} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {Store} from '@ngxs/store';
import {
  ConfigurationState,
  GeneralOptionsGroupName,
  InventoryService,
  PostingDataResponseModel,
  VehicleOption,
  EditPostingState,
  UpdatePosting,
  EditPostingFormNames
} from 'remarketing-angular-library';
import {OptionsAndAccessoriesFormsService} from '../../services/options-and-accessories-forms.service';
import {cloneDeep as _cloneDeep} from 'lodash';
import {SubGroupName, SubGroupResponseName} from '../../data/option-sub-group-mapping';
import {take, takeUntil} from 'rxjs/operators';
import {PostingFormDirective} from '../posting-form/posting-form.directive';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  OptionsAndAccessoriesFormComponent
} from '../options-and-accessories-form/options-and-accessories-form.component';


@Component({
    selector: 'app-exterior-form',
    templateUrl: './exterior-form.component.html',
    styleUrls: ['./exterior-form.component.scss'],
    imports: [TranslocoDirective, ReactiveFormsModule, OptionsAndAccessoriesFormComponent]
})
export class ExteriorFormComponent extends PostingFormDirective implements OnInit {
  exteriorForm: UntypedFormGroup;
  postingData: PostingDataResponseModel;
  originalPostingData: PostingDataResponseModel;
  originalOptions: VehicleOption[];
  vehicleWheelOption: VehicleOption[];
  vehicleWheelDisplayOption: VehicleOption[] = [];
  vehicleRoofOption: VehicleOption[];
  vehicleRoofDisplayOption: VehicleOption[] = [];
  otherOptions: VehicleOption[];
  additionalOptions: VehicleOption[];
  showAllOptions = false;
  originalVehicleWheels: VehicleOption[];
  originalVehiclelRoofs: VehicleOption[];
  @Input() postingTypeString: string;
  public trackFormChanges() {
    // Wheels
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.exteriorForm,
      'wheels',
      this.vehicleWheelOption,
      this.vehicleWheelDisplayOption
    );

    // Roof
    this.optionsAndAccessoriesFormsService.handleVehicleOptionDropdown(
      this.exteriorForm,
      'roof',
      this.vehicleRoofOption,
      this.vehicleRoofDisplayOption
    );

    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store,
    private readonly inventoryService: InventoryService,
    private readonly optionsAndAccessoriesFormsService: OptionsAndAccessoriesFormsService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService
  ) {
    super();
  }

  ngOnInit() {
    this.originalPostingData = _cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));

    // Store our original options so that they're always an available choice, regardless of if they come from the additional list
    this.originalOptions = this.originalPostingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions;
    this.originalVehicleWheels = this.originalPostingData.inventoryItem.vehicleOptions.exteriorOptions.wheels;
    this.originalVehiclelRoofs = this.originalPostingData.inventoryItem.vehicleOptions.exteriorOptions.roofs;

    this.exteriorForm = this.formBuilder.group({
      wheels: ['default'],
      roof: ['default']
    });
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = _cloneDeep(postingData);

        this.retrieveAllOptions();
        this.setAllOptionsToDefault();
      });

    // Set up our initial options, for when Show All Options isn't checked
    const options = [
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions,
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.tintedGlass
    ];
    this.otherOptions = this.optionsAndAccessoriesFormsService.getInitialOptions(options, this.originalOptions);
    const exteriorOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions;
    const selectedWheel = exteriorOptions.wheels?.find(wheel => wheel.installed);
    const selectedRoof = exteriorOptions.roofs?.find(roof => roof.installed);

    this.inventoryService
      .getVehicleGeneralOptions(GeneralOptionsGroupName.EXTERIOR)
      .pipe(take(1))
      .subscribe(x => {
        // Pull down Additional Options and select the Show All Options checkbox if we have any installed options from that list
        this.additionalOptions = x;
        if (
          this.otherOptions.filter(o => o.fromAllOptionsList).length > 0 ||
          selectedWheel?.fromAllOptionsList ||
          selectedRoof?.fromAllOptionsList
        ) {
          this.toggleAllOptions();
        } else {
          this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
            selectedWheel,
            SubGroupResponseName.WHEELS,
            this.originalVehicleWheels,
            exteriorOptions
          );
          this.optionsAndAccessoriesFormsService.setVehicleOptionsForNotShowAll(
            selectedRoof,
            SubGroupResponseName.ROOFS,
            this.originalVehiclelRoofs,
            exteriorOptions
          );
          this.resetAllDropdownsToDefault();
        }
      });
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.OPTIONS_EXTERIOR
    );
  }

  setDefaultWheelOption() {
    const installedWheels = this.postingData.inventoryItem.vehicleOptions.exteriorOptions.wheels?.find(
      w => w.installed
    );
    const displayWheel = this.vehicleWheelDisplayOption.find(
      w => w.optionCode === installedWheels?.optionCode && w.afterMarket === installedWheels?.afterMarket
    );
    this.exteriorForm.patchValue({
      wheels: displayWheel ? displayWheel : 'default'
    });
  }

  setDefaultRoofOption() {
    const installedRoof = this.postingData.inventoryItem.vehicleOptions.exteriorOptions.roofs?.find(r => r.installed);
    const displayRoof = this.vehicleRoofDisplayOption.find(
      r => r.optionCode === installedRoof?.optionCode && r.afterMarket === installedRoof?.afterMarket
    );
    this.exteriorForm.patchValue({
      roof: displayRoof ? displayRoof : 'default'
    });
  }

  // Handles showing/hiding additional options
  toggleAllOptions() {
    this.showAllOptions = !this.showAllOptions;
    //These groups are selected in the top dropdowns
    const invalidExteriorFormSubGroups = [SubGroupName.WHEELS, SubGroupName.ROOF];
    // Always include tinted glass
    this.otherOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions.concat(
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.tintedGlass
    );
    this.otherOptions = this.otherOptions.filter(emptyItem => emptyItem);
    this.resetAllDropdownOptions();

    if (this.showAllOptions) {
      // Show list of additional options and clean up any duplicates
      this.otherOptions = this.otherOptions.concat(
        this.additionalOptions?.filter(x => !invalidExteriorFormSubGroups.includes(x.subGroupName as SubGroupName))
      );
      this.otherOptions = this.optionsAndAccessoriesFormsService.cleanOptionsList(this.otherOptions);
      this.setAllExpandedDropdownList();
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.retrieveAllOptions();
      this.setAllOptionsToDefault();
    } else {
      this.otherOptions = this.otherOptions.filter(o => o.installed || o.fromAllOptionsList === false);
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions = this.otherOptions;
      this.setAllBaseDropdownList();
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.setInitialOptions();
      this.resetAllDropdownsToDefault();
      this.filterAllVehicleDisplayOptions();
    }
  }

  // Updates the vehicle's exterior otherOptions array and persists the update to the store
  installOptions(options: VehicleOption[]) {
    // if no options are selected, use otherOptions to persist the VehicleOption structure in the store
    if (options.length) {
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions = options;
    } else {
      this.postingData.inventoryItem.vehicleOptions.exteriorOptions.otherOptions = this.otherOptions;
    }
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  removeOption(option: VehicleOption) {
    if (!this.showAllOptions && option.fromAllOptionsList) {
      this.otherOptions = this.otherOptions.filter(other => other.optionCode !== option.optionCode);
    }
  }

  resetAllDropdownsToDefault() {
    this.retrieveAllOptions();
    this.optionsAndAccessoriesFormsService.configureDropdownList(
      this.vehicleWheelOption,
      this.vehicleWheelDisplayOption
    );
    this.optionsAndAccessoriesFormsService.configureDropdownList(this.vehicleRoofOption, this.vehicleRoofDisplayOption);
    this.setAllOptionsToDefault();
  }

  private resetAllDropdownOptions() {
    this.vehicleWheelOption = [];
    this.vehicleRoofOption = [];
    this.vehicleWheelDisplayOption = [];
    this.vehicleRoofDisplayOption = [];
  }

  retrieveAllOptions() {
    this.vehicleWheelOption = this.postingData.inventoryItem.vehicleOptions.exteriorOptions.wheels;
    this.vehicleRoofOption = this.postingData.inventoryItem.vehicleOptions.exteriorOptions.roofs;
  }

  setAllOptionsToDefault() {
    this.setDefaultWheelOption();
    this.setDefaultRoofOption();
  }

  setInitialOptions() {
    const exteriorOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions;
    const options: VehicleOption[][] = [exteriorOptions.otherOptions, exteriorOptions.tintedGlass];
    // Add the originally selected options as uninstalled so that they're always available, then clean the list to remove any duplicates
    this.otherOptions = this.optionsAndAccessoriesFormsService.getInitialOptions(options, this.originalOptions);
  }

  setAllExpandedDropdownList() {
    const exteriorOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions;
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.WHEELS,
      SubGroupResponseName.WHEELS,
      this.vehicleWheelOption,
      this.vehicleWheelDisplayOption,
      exteriorOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.setExpandedOptionsDropdownList(
      SubGroupName.ROOF,
      SubGroupResponseName.ROOFS,
      this.vehicleRoofOption,
      this.vehicleRoofDisplayOption,
      exteriorOptions,
      this.additionalOptions
    );
  }

  setAllBaseDropdownList() {
    const exteriorOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions;
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.WHEELS,
      this.originalVehicleWheels,
      exteriorOptions
    );
    this.optionsAndAccessoriesFormsService.setBaseOptionsDropdownList(
      SubGroupResponseName.ROOFS,
      this.originalVehiclelRoofs,
      exteriorOptions
    );
  }

  filterAllVehicleDisplayOptions() {
    const exteriorOptions = this.postingData.inventoryItem.vehicleOptions.exteriorOptions;
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.WHEELS,
      SubGroupResponseName.WHEELS,
      this.originalVehicleWheels,
      this.vehicleWheelDisplayOption,
      exteriorOptions,
      this.additionalOptions
    );
    this.optionsAndAccessoriesFormsService.filterVehicleDisplayOption(
      SubGroupName.ROOF,
      SubGroupResponseName.ROOFS,
      this.originalVehiclelRoofs,
      this.vehicleRoofDisplayOption,
      exteriorOptions,
      this.additionalOptions
    );
  }
}
